import { ButtonGroup, Card, CardContent } from '@mui/material';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { useIsBottomFromTenantAdmin } from 'shared/permissions/useIsBottomFromTenantAdmin';
import ConfirmationButtonDialog from 'components/ui/feedback/ConfirmationButtonDelete';
import { ProductWarehouseHistoryLinkTab } from 'features/cost-analysis';
import { periodicCostElementService } from 'features/cost-analysis/costs/api/periodic-cost-element.service';
import LocationForm from 'features/inventory/locations/components/LocationForm';
import { Location } from 'features/inventory/locations/types';
import { productWarehouseService } from 'features/inventory/warehouses/api/product-warehouse.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import WarehouseForm from 'features/inventory/warehouses/components/form/WarehouseForm';
import { IWarehouse } from 'features/inventory/warehouses/types';
import { operationalUnitService } from 'features/settings/topology/api/operational-unit.service';
import TenantPage from 'layouts/TenantPage';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { costElementService, laboratoryService, locationService } from 'shared/services';
import { BaseService } from 'shared/services/BaseService';
import toast from 'shared/utils/toast';
import { sectorService } from '../api/sector.service';
import LaboratoryForm from '../components/LaboratoryForm';
import OperationalUnitForm from '../components/OperationalUnitForm';
import SectorForm from '../components/SectorForm';
import TabTablePWLayer from '../components/tab-layer/TabTablePWLayer';
import { ILaboratory, IOperationalUnit, ISector } from '../types';
import { CostElementsColumns } from 'features/cost-analysis/costs/cost-elements';
import { ThresholdsColumns } from 'features/inventory/warehouses/thresholds';

function EditLayer() {
  const { layer, id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const costsPrefix = 'pages.cost.list';
  const isTenantAdminBottom = useIsBottomFromTenantAdmin();
  const isSuperAdminGuard = IsSuperAdminGuard();

  const [layerType, setLayerType] = useState<any>();
  const [service, setService] = useState<BaseService<any>>();
  const [entity, setEntity] = useState<
    ILaboratory | IOperationalUnit | ISector | IWarehouse | Location
  >();

  useEffect(() => {
    costElementService.getLayerOptions().then((layerTypes) => {
      const _layerType = layerTypes.find((lt) => lt.model === layer);
      setLayerType(_layerType.id);
    });
    // labelThresholdService.getAllBaseInformation().then((res) => {
    //   setLabelCodes(res.map((opt) => opt.code));
    // });
    // categoryService.getAllBaseInformation().then(setCategories);

    switch (layer) {
      case 'laboratory':
        setService(laboratoryService);
        break;
      case 'operationalunit':
        setService(operationalUnitService);
        break;
      case 'sector':
        setService(sectorService);
        break;
      case 'warehouse':
        setService(warehouseService);
        break;
      case 'location':
        setService(locationService);
        break;
    }
  }, []);

  const getOneShotCostElements = async (
    limit = 0,
    offset = 0,
    params: Record<string, unknown> = {}
  ) => {
    const res = await costElementService.getAllPaginated(limit, offset, {
      object_id: id,
      content_type: layerType,
      ...params
    });
    return res;
  };

  const getPeriodicCostElements = async (
    limit = 0,
    offset = 0,
    params: Record<string, unknown> = {}
  ) => {
    const res = await periodicCostElementService.getAllPaginated(limit, offset, {
      object_id: id,
      content_type: layerType,
      ...params
    });
    return res;
  };

  useEffect(() => {
    if (service) service.get(+id).then(setEntity);
  }, [service]);

  const deleteEntity = () => {
    service.delete(+id).then(() => {
      toast.success(t('actions.successfully.deleted'));
      navigate('..');
    });
  };

  const updateEntity = (values) => {
    if (!service || !entity?.id) {
      return;
    }

    service
      .update(entity.id, values)
      .then(() => service.get(+id).then(setEntity))
      .then(() => enqueueSnackbar(t('messages.entity-updated'), { variant: 'success' }));
  };

  const columns = CostElementsColumns();
  const thresholdColumns = ThresholdsColumns(false, null, null);
  const periodicCostColumns = CostElementsColumns(true);

  const tabConfig: TabItem[] = useMemo(() => {
    return [
      {
        label: t('global.details'),
        component: (
          <div>
            {layer === 'laboratory' && (
              <LaboratoryForm laboratory={entity} onSubmit={updateEntity} />
            )}
            {layer === 'operationalunit' && (
              <OperationalUnitForm
                operational_unit={entity as IOperationalUnit}
                onSubmit={updateEntity}
              />
            )}
            {layer === 'sector' && (
              <SectorForm sector={entity as ISector} onSubmit={updateEntity} />
            )}
            {layer === 'warehouse' && (
              <div>
                <WarehouseForm
                  warehouse={entity as IWarehouse}
                  onSubmit={updateEntity}
                  disableEdit={!isTenantAdminBottom}
                />
                <div className="w-full p-4">
                  <Card variant="outlined">
                    <CardContent>
                      <b className="text-primary-600">{t('menu.thresholds')}</b>
                      <br />
                      <EnhancedDataGrid
                        service={productWarehouseService}
                        columns={thresholdColumns}
                        outerParams={{
                          warehouse_id: id
                        }}
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
            {layer === 'location' && (
              <LocationForm
                location={entity as Location}
                onSubmit={updateEntity}
                disableEdit={!isTenantAdminBottom}
              />
            )}
          </div>
        )
      },
      {
        label: t('global.products'),
        component: (
          <TabTablePWLayer
            title={t('global.products')}
            addElementLabel={t('pages.exams.tabs.products.add_element')}
            selectElementLabel={t('global.product')}
            currentElement={entity}
            disableEdit={!isTenantAdminBottom}
            onSubmit={(ids) => updateEntity({ product_warehouses: ids })}
          />
        )
      },
      {
        label: t(`${costsPrefix}.extraordinary_costs`),
        visible: !!layerType,
        component: (
          <EnhancedDataGrid
            columns={columns}
            service={costElementService}
            getMethod={getOneShotCostElements}
          />
        )
      },
      {
        label: t(`${costsPrefix}.periodic_costs`),
        visible: !!layerType,
        component: (
          <EnhancedDataGrid
            columns={periodicCostColumns}
            service={periodicCostElementService}
            getMethod={getPeriodicCostElements}
          />
        )
      },
      {
        label: t('global.history-update'),
        visible: isSuperAdminGuard && !!layerType,
        component: entity ? (
          <div>
            <ProductWarehouseHistoryLinkTab
              title={t(`global.${layer}`)}
              layer={layer}
              entity_id={entity.id}
            />
          </div>
        ) : null
      }
    ];
  }, [
    entity,
    layer,
    layerType,
    id,
    isTenantAdminBottom,
    isSuperAdminGuard,
    t,
    columns,
    periodicCostColumns,
    thresholdColumns,
    getOneShotCostElements,
    getPeriodicCostElements,
    updateEntity
  ]);

  return (
    <TenantPage
      title={t(`global.${layer}`)}
      subtitle={''}
      entityCode={entity?.code}
      menuRight={
        <ButtonGroup>
          <ConfirmationButtonDialog handleDelete={deleteEntity} />
        </ButtonGroup>
      }>
      <EnhancedTabs tabs={tabConfig} variant="scrollable" allowScrollButtonsMobile />
    </TenantPage>
  );
}

export default EditLayer;
